<template>
    <BaseDropdownMenu :sections="menuSections" align="start" side="top" aria-label="Settings" @select="handleSelect">
        <template #trigger>
            <BaseTooltip side="right" :disabled="showLabel">
                <template #trigger>
                    <button
                        data-testid="settings-button"
                        type="button"
                        aria-label="Settings"
                        class="flex w-full text-nav-item-text hover:text-nav-item-text-hover transition-colors flex-row items-center gap-3">
                        <i class="bi bi-gear nav-icon h-8" aria-hidden="true" />
                        <span v-if="showLabel" class="body-small"> Settings </span>
                    </button>
                </template>
                <template #content>Settings</template>
            </BaseTooltip>
        </template>
    </BaseDropdownMenu>
    <form ref="logoutForm" method="POST" :action="logoutUrl" style="display: none">
        <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
    </form>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import type { GlobalProperties } from "~vue/appTypes";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import AlertCircle from "~vue/icons/AlertCircle.vue";
import Logout from "~vue/icons/Logout.vue";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { inject, useTemplateRef } from "vue";

type SettingsMenuProps = {
    showLabel?: boolean;
};

const { showLabel = true } = defineProps<SettingsMenuProps>();

const globalProperties = inject<GlobalProperties>("globalProperties");

const feedbackUrl = isInIFrame() ? "/microsoft-teams/report-issue/" : (globalProperties?.$feedbackMailto ?? "");
const logoutUrl = isInIFrame() ? "/accounts/logout/?next=/microsoft-teams/login/" : "/accounts/logout/";

const menuSections: DropdownMenuSection[] = [
    {
        id: "settings",
        items: [
            {
                id: "profile",
                type: "inertia-link",
                href: "/accounts/user-profile",
                label: "Profile",
                icon: "bi bi-person-circle",
                testId: "profile",
                itemClass: "md:hidden",
            },
            { id: "preferences", type: "inertia-link", href: "/accounts/user-preferences", label: "Preferences", icon: "bi bi-sliders", testId: "preferences" },
            { id: "feedback", type: "anchor", href: feedbackUrl, label: "Report an Issue", icon: AlertCircle, testId: "feedback" },
            { id: "logout", type: "button", label: "Logout", icon: Logout, testId: "logout" },
        ],
    },
];

const logoutForm = useTemplateRef<HTMLFormElement>("logoutForm");

const handleSelect = (item: DropdownMenuItem) => {
    if (item.id === "feedback") {
        logUserInteraction("navigation_report_issue_clicked");
    }
    if (item.id === "logout") {
        // Drop the current entry so the back button after logout doesn't restore an authenticated page.
        globalThis.history.replaceState(null, "", globalThis.location.href);
        logoutForm.value?.submit();
    }
};
</script>
